'use client';

import * as React from 'react';

import * as TooltipPrimitive from '@radix-ui/react-tooltip';

import { useMediaQuery } from '@/hooks/use-mobile';
import { cn } from '@/lib/utils';

type TooltipProviderProps = React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Provider>;

function TooltipProvider({ delayDuration = 0, ...props }: TooltipProviderProps) {
  return <TooltipPrimitive.Provider delayDuration={delayDuration} {...props} />;
}

type TooltipContextValue = {
  isTouchControlled: boolean;
  open: boolean;
  onOpenChange?: (open: boolean) => void;
  triggerRef: React.RefObject<HTMLElement | null>;
};

const TooltipContext = React.createContext<TooltipContextValue | null>(null);

function useTooltipContext(): TooltipContextValue {
  const context = React.useContext(TooltipContext);
  if (!context) {
    throw new Error('Tooltip components must be used within a Tooltip');
  }
  return context;
}

type TooltipProps = React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Root>;

function Tooltip({ children, open, onOpenChange, ...props }: TooltipProps) {
  const isTouchDevice = useMediaQuery('(hover: none) and (pointer: coarse)');
  const isTouchControlled = isTouchDevice && open !== undefined;
  const triggerRef = React.useRef<HTMLElement | null>(null);

  const contextValue = React.useMemo<TooltipContextValue>(
    () => ({ isTouchControlled, open: open ?? false, onOpenChange, triggerRef }),
    [isTouchControlled, open, onOpenChange],
  );

  return (
    <TooltipContext.Provider value={contextValue}>
      <TooltipPrimitive.Root
        open={open}
        onOpenChange={isTouchControlled ? undefined : onOpenChange}
        {...props}
      >
        {children}
      </TooltipPrimitive.Root>
    </TooltipContext.Provider>
  );
}

type TooltipTriggerProps = React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Trigger>;

const TooltipTrigger = React.forwardRef<HTMLButtonElement, TooltipTriggerProps>(
  function TooltipTrigger({ onClick, ...props }, forwardedRef) {
    const { isTouchControlled, open, onOpenChange, triggerRef } = useTooltipContext();

    const handleRef = (node: HTMLButtonElement | null) => {
      triggerRef.current = node;
      if (typeof forwardedRef === 'function') forwardedRef(node);
      else if (forwardedRef) forwardedRef.current = node;
    };

    const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
      if (isTouchControlled) {
        e.preventDefault();
        e.stopPropagation();
        onOpenChange?.(!open);
      }
      onClick?.(e);
    };

    return (
      <TooltipPrimitive.Trigger
        ref={handleRef}
        data-slot="tooltip-trigger"
        onClick={handleClick}
        {...props}
      />
    );
  },
);
TooltipTrigger.displayName = 'TooltipTrigger';

type TooltipContentProps = React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content>;

const TooltipContent = React.forwardRef<HTMLDivElement, TooltipContentProps>(
  function TooltipContent(
    { className, sideOffset = 0, collisionPadding = 16, children, onPointerDownOutside, ...props },
    ref,
  ) {
    const { isTouchControlled, onOpenChange, triggerRef } = useTooltipContext();

    const handlePointerDownOutside = (
      e: Parameters<NonNullable<typeof onPointerDownOutside>>[0],
    ) => {
      if (triggerRef.current?.contains(e.target as Node)) {
        e.preventDefault();
        return;
      }
      if (isTouchControlled) onOpenChange?.(false);
      onPointerDownOutside?.(e);
    };

    return (
      <TooltipPrimitive.Portal>
        <TooltipPrimitive.Content
          ref={ref}
          data-slot="tooltip-content"
          sideOffset={sideOffset}
          collisionPadding={collisionPadding}
          onPointerDownOutside={handlePointerDownOutside}
          className={cn(
            'z-50 w-fit origin-(--radix-tooltip-content-transform-origin) animate-in rounded-md bg-primary px-3 py-1.5 text-xs text-primary-foreground fade-in-0 zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 rtl:data-[side=left]:slide-in-from-left-2 rtl:data-[side=right]:slide-in-from-right-2',
            isTouchControlled && 'pointer-events-auto',
            className,
          )}
          {...props}
        >
          {children}
          <TooltipPrimitive.Arrow className="z-50 size-2.5 translate-y-[calc(-50%-2px)] rotate-45 rounded-[2px] bg-primary fill-primary" />
        </TooltipPrimitive.Content>
      </TooltipPrimitive.Portal>
    );
  },
);
TooltipContent.displayName = 'TooltipContent';

export { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider };
